import { useEffect, useState } from "react";
import { ShieldCheck, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const input = "w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";

export const ForceTwoFASetup = () => {
  const { setUser, logout } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);

  useEffect(() => {
    api.post("/auth/2fa/setup").then((r) => setSetup(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const enable = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setBackupCodes(data.backup_codes || []);
      toast.success("2FA ativado com sucesso");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const finish = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 flex items-center justify-center p-6" data-testid="force-2fa-page">
      <div className="w-full max-w-lg border border-zinc-800 rounded-lg p-8">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={24} className="text-emerald-400" weight="bold" />
          <h1 className="font-display text-xl font-semibold">Autenticação em 2 Etapas Obrigatória</h1>
        </div>
        <p className="text-sm text-zinc-400 mb-6">
          Por segurança, contas de administrador e usuário completo precisam ativar o 2FA antes de acessar o sistema.
          Use um app autenticador (Google Authenticator, Authy, etc.).
        </p>

        {backupCodes ? (
          <div data-testid="force-2fa-backup-codes">
            <p className="text-sm text-amber-400 mb-3 font-medium">Guarde seus códigos de backup — eles permitem entrar se você perder o celular:</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {backupCodes.map((c) => (
                <code key={c} className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-xs text-indigo-300 text-center">{c}</code>
              ))}
            </div>
            <button data-testid="force-2fa-copy-codes" onClick={() => { navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Códigos copiados"); }}
              className="w-full flex items-center justify-center gap-2 border border-zinc-700 hover:bg-zinc-900 text-sm py-2.5 rounded mb-3 transition-colors">
              <Copy size={15} /> Copiar códigos
            </button>
            <button data-testid="force-2fa-continue" onClick={finish}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-2.5 rounded transition-colors">
              Continuar para o sistema
            </button>
          </div>
        ) : setup ? (
          <form onSubmit={enable}>
            <div className="flex flex-col items-center mb-4">
              <img data-testid="force-2fa-qr" src={setup.qr_code} alt="QR Code 2FA" className="w-44 h-44 rounded bg-white p-2 mb-3" />
              <p className="text-xs text-zinc-500 mb-1">Não consegue escanear? Digite a chave manualmente:</p>
              <code data-testid="force-2fa-secret" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-xs text-indigo-300 break-all">{setup.secret}</code>
            </div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">Código de 6 dígitos do app</label>
            <input data-testid="force-2fa-code-input" className={`${input} mb-3 text-center tracking-[0.3em]`} value={code} maxLength={6}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" required />
            <button data-testid="force-2fa-enable-button"
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-medium py-2.5 rounded transition-colors">
              Ativar 2FA e entrar
            </button>
          </form>
        ) : (
          <p className="text-zinc-500 text-sm">Gerando QR Code...</p>
        )}

        <button data-testid="force-2fa-logout" onClick={logout}
          className="w-full text-xs text-zinc-500 hover:text-zinc-300 mt-4 transition-colors">
          Sair e entrar com outra conta
        </button>
      </div>
    </div>
  );
};
